import {
  MdAddCard,
  MdCheck,
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdOutlineCompareArrows,
  MdOutlineCreditCard,
  MdTune,
} from "react-icons/md";
import { BUTTON_STYLES, ICON_SIZES } from "../helpers/constants";
import useMainFilter from "./useMainFilter";
import clsx from "clsx";
import { orderBy, sortBy } from "lodash-es";
import { rgbaObjectToString } from "../helpers/utils";
import useCurrentUser from "./useCurrentUser";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useMobile from "./useMobile";
import useCollapsibles from "./useCollapsibles";
import useAccountLocalStorage from "./useAccountLocalStorage";
import useModalsStore from "src/store/modalsStore";
import { useMemo, useTransition } from "react";

export default function useAccountsDropdownMenu({
  linkTo,
}: {
  linkTo:
    | "reports"
    | "transactions"
    | "transaction"
    | "recycle"
    | "attachments"
    | "recurring";
}) {
  const { isMobile } = useMobile();
  const navigate = useNavigate();
  const { accounts, isLoadingAccounts } = useCurrentUser();
  const { t } = useTranslation();
  const { collapsiblePreferences, updateCollapsible } = useCollapsibles();
  const { updateAccountLocalStorage } = useAccountLocalStorage();
  const { setShowMoneyTransferModal } = useModalsStore();
  const [isPending, startTransition] = useTransition();

  const { mainFilter, setMainFilter } = useMainFilter();

  const sortedOptions = useMemo(() => {
    if (isLoadingAccounts) return [];

    return orderBy(
      [
        {
          icon: (
            <MdOutlineCompareArrows
              size={ICON_SIZES.MD}
              className={clsx(["text-gray-800", "dark:text-gray-300"])}
            />
          ),
          text: t("Transferir fondos"),
          onClick: () => setShowMoneyTransferModal(true),
          disabled: !mainFilter?.account,
        },
        {
          icon: collapsiblePreferences.accountOpen ? (
            <MdCheckBox
              size={ICON_SIZES.MD}
              className={clsx(["text-gray-800", "dark:text-gray-300"])}
            />
          ) : (
            <MdCheckBoxOutlineBlank
              size={ICON_SIZES.MD}
              className={clsx(["text-gray-800", "dark:text-gray-300"])}
            />
          ),
          hide: !isMobile,
          text: t("Mostrar la cuenta activa"),
          onClick: () => {
            updateCollapsible({
              accountOpen: !collapsiblePreferences.accountOpen,
            });
          },
        },
        {
          icon: (
            <MdAddCard
              size={ICON_SIZES.MD}
              className={clsx(["text-gray-800", "dark:text-gray-300"])}
            />
          ),
          text: t("shared.add_an_account"),
          onClick: () => {
            startTransition(() => navigate(`/settings/accounts/new`));
          },
          hide: true,
        },
        {
          icon: (
            <MdTune
              size={ICON_SIZES.MD}
              className={clsx(["text-gray-800", "dark:text-gray-300"])}
            />
          ),
          text: t("Ajustes"),
          onClick: () => {
            startTransition(() => navigate(`/settings/accounts/`));
          },
        },
      ],
      ["text"],
      ["asc"]
    );
  }, [
    mainFilter?.account,
    collapsiblePreferences?.accountOpen,
    isMobile,
    isLoadingAccounts,
  ]);

  return {
    icon: (
      <MdOutlineCreditCard
        size={ICON_SIZES.MD}
        className={clsx(["text-red-500", "dark:text-purple-500"])}
      />
    ),
    tooltip: "Cuentas",
    className: isMobile ? clsx([BUTTON_STYLES.HEADING]) : "",
    options: isLoadingAccounts
      ? []
      : [
          ...sortBy(
            accounts?.data?.data
              ?.filter?.(
                (leAccount: any) => leAccount?.settings?.disabled !== true
              )
              ?.map?.((account: any) => ({
                text: account.name,
                onClick: () => {
                  if (account?.account !== mainFilter.account) {
                    updateAccountLocalStorage({
                      lastAccount: account?.account,
                    });

                    startTransition(() =>
                      setMainFilter({ account: account?.account })
                    );
                    startTransition(() =>
                      navigate(`/${linkTo}/${account?.account}`, {
                        replace: true,
                      })
                    );
                  }
                },
                className:
                  mainFilter?.account === account.account
                    ? clsx(["font-medium"])
                    : "",
                icon: (
                  <div
                    className={clsx([
                      "flex",
                      "rounded-full",
                      `h-[32px]`,
                      `w-[32px]`,
                      "items-center",
                      "justify-center",
                    ])}
                    style={{
                      backgroundColor: rgbaObjectToString(
                        account?.settings?.bg
                      ),
                    }}
                  >
                    {mainFilter?.account === account.account ? (
                      <MdCheck
                        size={ICON_SIZES.SM}
                        style={{
                          color: rgbaObjectToString(account?.settings?.color),
                        }}
                      />
                    ) : (
                      <></>
                    )}
                  </div>
                ),
              })) || [],
            "text"
          ),
          ...sortedOptions,
        ],
    isLoading: !!isLoadingAccounts,
    disabled: !!isLoadingAccounts,
  };
}
